import React, { useEffect, useMemo, useState } from "react";

/**
 * Experian Credit Report renderer
 *
 * This component intentionally uses plain CSS instead of Tailwind for the report
 * itself. The reference PDF is an A4, print-oriented document with a serif
 * typeface, thin table borders, blue labels, grey section bars and a repeated
 * header/footer on every page.
 */

const EXPERIAN_LOGO_URL =
  "https://www.experian.com/static/shared/logos/logo-experian.svg";

const STATE_CODE_MAP = {
  "01": "Jammu and Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  "10": "Bihar",
  "11": "Sikkim",
  "12": "Arunachal Pradesh",
  "13": "Nagaland",
  "14": "Manipur",
  "15": "Mizoram",
  "16": "Tripura",
  "17": "Meghalaya",
  "18": "Assam",
  "19": "West Bengal",
  "20": "Jharkhand",
  "21": "Odisha",
  "22": "Chhattisgarh",
  "23": "Madhya Pradesh",
  "24": "Gujarat",
  "25": "Daman and Diu",
  "26": "Dadra and Nagar Haveli",
  "27": "Maharashtra",
  "28": "Andhra Pradesh (Old)",
  "29": "Karnataka",
  "30": "Goa",
  "31": "Lakshadweep",
  "32": "Kerala",
  "33": "Tamil Nadu",
  "34": "Puducherry",
  "35": "Andaman and Nicobar Islands",
  "36": "Telangana",
  "37": "Andhra Pradesh",
  "97": "Other Territory",
  "99": "Other",
};

const ACCOUNT_TYPE_MAP = {
  "01": "Auto - Loan (Personal)",
  "02": "Housing Loan",
  "03": "Property Loan",
  "04": "Loan Against Shares/Securities",
  "05": "Personal Loan",
  "06": "Consumer Loan",
  "07": "Gold Loan",
  "08": "Education Loan",
  "09": "Loan to Professional",
  "10": "Credit Card",
  "11": "Leasing",
  "12": "Overdraft",
  "13": "Two-Wheeler Loan",
  "14": "Non-Funded Credit Facility",
  "15": "Loan Against Bank Deposits",
  "16": "Fleet Card",
  "17": "Commercial Vehicle Loan",
  "18": "Telco - Wireless",
  "19": "Telco - Broadband",
  "20": "Telco - Landline",
  "21": "Seller Financing",
  "23": "GECL Loan Secured",
  "24": "GECL Loan Unsecured",
  "31": "Secured Credit Card",
  "32": "Used Car Loan",
  "33": "Construction Equipment Loan",
  "34": "Tractor Loan",
  "35": "Corporate Credit Card",
  "36": "Kisan Credit Card",
  "37": "Loan on Credit Card",
  "38": "Prime Minister Jaan Dhan Yojana - Overdraft",
  "39": "Mudra Loans - Shishu/Kishor/Tarun",
  "40": "Microfinance - Business Loan",
  "41": "Microfinance - Personal Loan",
  "42": "Microfinance - Housing Loan",
  "43": "Microfinance - Others",
  "44":
    "Pradhan Mantri Awas Yojana - Credit Link Subsidy Scheme MAY CLSS",
  "45": "P2P Personal Loan",
  "46": "P2P Auto Loan",
  "47": "P2P Education Loan",
  "50": "Business Loan - Secured",
  "51": "Business Loan - General",
  "52": "Business Loan - Priority Sector - Small Business",
  "53": "Business Loan - Priority Sector - Agriculture",
  "54": "Business Loan - Priority Sector - Others",
  "55": "Business Non-Funded Credit Facility - General",
  "56":
    "Business Non-Funded Credit Facility - Priority Sector - Small Business",
  "57":
    "Business Non-Funded Credit Facility - Priority Sector - Agriculture",
  "58": "Business Non-Funded Credit Facility - Priority Sector - Others",
  "59": "Business Loan Against Bank Deposits",
  "61": "Business Loan - Unsecured",
  "69": "Short Term Personal Loan",
  "70": "Priority Sector- Gold Loan",
  "71": "Temporary Overdraft",
  "00": "Other",
};

const MONTHS = [
  { short: "Dec", number: 12 },
  { short: "Nov", number: 11 },
  { short: "Oct", number: 10 },
  { short: "Sep", number: 9 },
  { short: "Aug", number: 8 },
  { short: "Jul", number: 7 },
  { short: "Jun", number: 6 },
  { short: "May", number: 5 },
  { short: "Apr", number: 4 },
  { short: "Mar", number: 3 },
  { short: "Feb", number: 2 },
  { short: "Jan", number: 1 },
];

const SECTION_DESCRIPTION = {
  currentApplication:
    "These are the details you give us when you apply for your Experian Credit Report.",
  accountSummary:
    "This section displays summary of all your reported credit accounts found in the Experian Credit Bureau database.",
  accountDetails:
    "This section has information based on the details provided to Experian by all our member banks, credit / financial institutions and other credit grantors with whom you have a credit / loan account.",
  enquiries:
    "This section shows the names of the credit institutions that have processed a credit / loan application for you.",
};

const CREDIT_SCORE_FACTORS = [
  ["Recency", "Recent Credit Account Defaults"],
  ["Leverage", "Credit Accounts with on-time re-payment history"],
  ["Coverage", "Non-delinquent and delinquent Credit Accounts"],
  [
    "Delinquency Status",
    "Defaults on Credit Accounts (current & recent periodic intervals)",
  ],
];

const LEGEND_ITEMS = [
  ["Active", "Credit Accounts that do not have 'Date Closed' populated."],
  ["Closed", "Credit accounts that have 'Date Closed' populated."],
  ["CO", "Entity ceased while account was open."],
  ["CC", "Entity ceased while account was closed."],
  [
    "DPD",
    "Days Past Due. Number of days that have passed from the agreed payment due date of EMI.",
  ],
  [
    "DPD '0'",
    "The number '0' in the circle indicates that the Payment is made as agreed and the credit account is up to date.",
  ],
  [
    "DPD >0",
    "The number in the circle indicates the 'Days Past Due' reported by the respective lender.",
  ],
];

const ASSET_CLASSIFICATION_ITEMS = [
  [
    "S",
    "Standard: An account which is overdue for less than 90 days is considered as a Standard asset as per RBI guidelines.",
  ],
  [
    "M",
    "Special Mention: These accounts are \"Standard\" assets but for some discrepancy the lending organization would like to monitor this account closely.",
  ],
  [
    "B",
    "Substandard: An account which is overdue by 90 days for a period of less than or equal to 12 months is classified as Substandard asset as per RBI guidelines.",
  ],
  [
    "D",
    "Doubtful: An account which is overdue by 90 days for more than 12 months is classified as doubtful asset as per RBI guidelines.",
  ],
  [
    "L",
    "Loss: An account where loss has been identified but the amount has not been written off, wholly or partially is classified as Loss asset as per RBI guidelines.",
  ],
];

const REPORT_DISCLAIMER = `Experian Credit Information Company of India Private Limited (\"ECICI\") is registered as a Credit Information Company with the Reserve Bank of India. ECICI has empanelled several credit institutions / specified users (\"Members\") with it, from whom it collects information / data for the bureau operations. The current Experian Credit Information Report and Experian Credit Score (\"Report\") is a reflection of this information / data as submitted by the Member to ECICI. The Report may thus be limited to the information / data contributed by the Member. The information / data provided in the Report is as current and up to date as provided by the Members. The Report is not a guarantee of any particular outcome and you may therefore not use any information, in full or in part, as the sole basis for any decision you take. ECICI does not accept any responsibility for any loss that may arise to you from relying on the information / data in the Report. ECICI will use all reasonable skill and care in the supply of the information / data to you. However since we obtain the information / data from the Member, ECICI cannot guarantee or warranty that the information contained in the Report is complete, accurate, up-to-date or error-free. Any information contained herein does not reflect the views of ECICI or its directors or employees.

This message contains legally privileged and/or confidential information. If you are not the intended recipient(s) of this message, you are hereby notified that any dissemination, distribution or copying of this Report is strictly prohibited. If you have received this Report in error, please notify the sender immediately and delete this Report from your computer and all your records.`;

const REPORT_CSS = `
  @page {
    size: A4 portrait;
    margin: 0;
  }

  :root {
    --exp-blue: #0070c0;
    --exp-dark-blue: #005a9e;
    --exp-grey: #efefef;
    --exp-border: #d7d7d7;
    --exp-green: #40951d;
    --exp-orange: #c9854a;
    --exp-text: #202020;
  }

  * {
    box-sizing: border-box;
  }

  html,
  body {
    margin: 0;
    padding: 0;
    background: #f2f2f2;
    color: var(--exp-text);
    font-family: "Times New Roman", Times, serif;
    font-size: 10px;
    line-height: 1.18;
  }

  body {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .experian-modal-scroll {
    width: min(100%, 850px);
    max-height: calc(100vh - 96px);
    overflow: auto;
    scrollbar-width: thin;
  }

  .experian-report {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    background: #fff;
    padding: 7mm 3.7mm 14mm;
    box-shadow: 0 3px 18px rgba(0, 0, 0, .12);
    position: relative;
  }

  .experian-print-header {
    height: 27mm;
    border-bottom: 1px solid #d9d9d9;
    position: relative;
  }

  .experian-print-header img {
    position: absolute;
    left: 3px;
    top: 0;
    width: 31mm;
    max-width: 122px;
    height: auto;
    display: block;
  }

  .experian-print-title {
    position: absolute;
    right: 1px;
    top: 0;
    color: var(--exp-blue);
    font-family: "Times New Roman", Times, serif;
    font-size: 22px;
    line-height: 1;
    white-space: nowrap;
  }

  .experian-report-meta {
    position: absolute;
    right: 1px;
    top: 15.2mm;
    color: var(--exp-blue);
    text-align: right;
    font-size: 10px;
    line-height: 1.28;
  }

  .experian-report-meta strong {
    font-weight: 400;
  }

  .experian-page-content {
    padding-top: 3.2mm;
  }

  .experian-print-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 2.4mm;
    height: 7mm;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    color: #333;
    font-size: 9px;
    border-top: 1px solid #d0d0d0;
    padding-top: 1.6mm;
  }

  .experian-section {
    border: 1px solid var(--exp-border);
    margin-bottom: 4px;
    background: #fff;
    break-inside: auto;
    page-break-inside: auto;
  }

  .experian-section-header {
    min-height: 18px;
    background: linear-gradient(#f7f7f7, #e8e8e8);
    border-bottom: 1px solid #d8d8d8;
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 2px 6px;
    font-weight: 700;
    color: #3c3c3c;
    font-size: 11px;
    line-height: 1.05;
    page-break-after: avoid;
  }

  .experian-section-check {
    width: 14px;
    height: 14px;
    min-width: 14px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--exp-green);
    color: white;
    font-family: Arial, sans-serif;
    font-size: 10px;
    font-weight: 700;
    box-shadow: inset 0 -1px 0 rgba(0,0,0,.08);
  }

  .experian-section-title {
    flex: 1;
  }

  .experian-section-right {
    color: var(--exp-blue);
    font-size: 10px;
    white-space: nowrap;
    margin-left: auto;
  }

  .experian-note {
    padding: 4px 7px 2px;
    color: var(--exp-orange);
    font-style: italic;
    font-size: 10px;
  }

  .exp-current-info {
    padding: 1px 7px 7px;
  }

  .exp-kv-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .exp-kv-table td {
    vertical-align: top;
    padding: 2px 4px 2px 0;
    border: 0;
    font-size: 10px;
    line-height: 1.18;
  }

  .exp-kv-table td.label {
    width: 15%;
    color: var(--exp-blue);
    font-weight: 700;
    white-space: nowrap;
  }

  .exp-kv-table td.value {
    width: 18.5%;
  }

  .exp-kv-table td.spacer {
    width: 2.5%;
  }

  .exp-score-wrap {
    padding: 10px 10px 13px;
  }

  .exp-score-intro {
    color: var(--exp-orange);
    font-style: italic;
    font-size: 10px;
    margin-bottom: 11px;
  }

  .exp-score-layout {
    display: grid;
    grid-template-columns: 28% 1fr;
    gap: 10mm;
    align-items: center;
  }

  .exp-score-box {
    width: 46mm;
    min-height: 29mm;
    margin: 0 auto;
    border: 1px solid #1d1d1d;
    text-align: center;
    padding: 8px 5px 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .exp-score-box .caption {
    font-size: 10px;
    line-height: 1.05;
  }

  .exp-score-box .number {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 19px;
    font-weight: 700;
    margin: 7px 0 2px;
  }

  .exp-score-box .range {
    font-size: 10px;
    font-style: italic;
  }

  .exp-factors {
    border: 1px solid #d3d3d3;
    min-height: 29mm;
    padding: 4px 7px 3px;
  }

  .exp-factors-title {
    margin-bottom: 3px;
    font-size: 10px;
  }

  .exp-factor-row {
    display: grid;
    grid-template-columns: 18px 118px 1fr;
    column-gap: 2px;
    margin: 3px 0;
    font-size: 10px;
  }

  .exp-factor-label {
    font-weight: 700;
  }

  .exp-summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    border-top: 1px solid var(--exp-border);
  }

  .exp-summary-col {
    min-height: 31mm;
    padding: 5px 8px 7px;
    border-right: 1px solid var(--exp-border);
  }

  .exp-summary-col:last-child {
    border-right: 0;
  }

  .exp-summary-title {
    color: var(--exp-blue);
    font-weight: 700;
    margin-bottom: 4px;
    font-size: 10px;
  }

  .exp-summary-row {
    display: grid;
    grid-template-columns: 1fr 50px;
    column-gap: 8px;
    padding: 3px 0;
    font-size: 10px;
  }

  .exp-summary-row .label {
    color: var(--exp-blue);
  }

  .exp-summary-row .value {
    text-align: right;
  }

  .exp-account-summary {
    padding: 4px 4px 3px;
  }

  .exp-account-summary table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .exp-account-summary th {
    color: var(--exp-blue);
    font-weight: 700;
    text-align: left;
    padding: 4px 4px 3px;
    border-bottom: 0;
    font-size: 9px;
    line-height: 1.1;
    vertical-align: top;
  }

  .exp-account-summary td {
    padding: 2px 4px;
    border: 0;
    vertical-align: top;
    font-size: 10px;
    line-height: 1.05;
  }

  .exp-account-summary .serial {
    color: var(--exp-blue);
    font-weight: 700;
    width: 28px;
  }

  .exp-summary-grid-table {
    border: 1px solid var(--exp-border);
  }

  .exp-account-block {
  padding: 20px 0;
    border: 1px solid var(--exp-border);
    margin: 20px 0 4px;
    background: #fff;
    break-inside: auto;
    page-break-inside: auto;
  }

  .exp-account-heading {
    padding: 6px 6px 4px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .exp-account-topline {
    display: grid;
    text-transform: uppercase;
    grid-template-columns: 31% 51% 18%;
    align-items: start;
    column-gap: 4px;
    color: var(--exp-blue);
    font-weight: 700;
    font-size: 10px;
    line-height: 1.1;
  }

  .exp-account-name {
    margin-top: 8px;
    color: #2d2d2d;
    font-size: 10px;
    font-weight: 700;
  }

  .exp-account-act {
    display: inline-flex;
    align-items: center;
    justify-content: start;
    gap: 4px;
    white-space: nowrap;
    text-align: right;
  }

  .exp-account-act .circle {
    width: 15px;
    height: 15px;
    min-width: 15px;
    border-radius: 50%;
    background: var(--exp-green);
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: Arial, sans-serif;
    font-size: 10px;
  }

  .exp-account-underline {
    height: 3px;
    width: 61%;
    background: #e1c6aa;
    margin-top: 6px;
  }

  .exp-account-address {
    display: grid;
    grid-template-columns: 110px 1fr;
    column-gap: 4px;
    border-top: 0;
    padding-top: 4px;
    min-height: 15mm;
    font-size: 10px;
  }

  .exp-account-address .label {
    color: var(--exp-blue);
    font-weight: 700;
  }

  .exp-account-address .value {
    word-break: break-word;
  }

  .exp-subheading {
    border-top: 1px solid var(--exp-border);
    color: var(--exp-blue);
    font-weight: 700;
    padding: 5px 6px 3px;
    font-size: 10px;
    break-after: avoid;
    page-break-after: avoid;
  }

  .exp-terms-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    border-top: 1px solid var(--exp-border);
    border-bottom: 1px solid var(--exp-border);
  }

  .exp-terms-table th,
  .exp-terms-table td {
    border-right: 1px solid var(--exp-border);
    padding: 3px 6px;
    vertical-align: top;
    font-size: 10px;
    line-height: 1.12;
  }

  .exp-terms-table th:last-child,
  .exp-terms-table td:last-child {
    border-right: 0;
  }

  .exp-terms-table th {
    color: var(--exp-blue);
    text-align: left;
    font-weight: 700;
    border-bottom: 1px solid #dedede;
    height: 20px;
  }

  .exp-term-row {
    display: grid;
    grid-template-columns: 1.65fr 1fr;
    gap: 5px;
    padding: 2px 0;
  }

  .exp-term-label {
    color: var(--exp-blue);
    font-weight: 700;
  }

  .exp-term-value {
    color: #2d2d2d;
    word-break: break-word;
  }

  .exp-payment {
    padding: 0 0 15px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .exp-payment-title,
  .exp-personal-title {
    color: var(--exp-blue);
    font-weight: 700;
    padding: 4px 6px 2px;
    font-size: 10px;
  }

  .exp-payment-table {
    border-collapse: collapse;
    margin: 0 0 3px 0;
    table-layout: fixed;
  }

  .exp-payment-table th,
  .exp-payment-table td {
    border: 1px solid #d7dfe7;
    text-align: center;
    height: 21px;
    min-width: 36px;
    padding: 1px;
    font-size: 9px;
  }

  .exp-payment-table th {
    color: var(--exp-blue);
    font-weight: 700;
    background: #fafafa;
  }

  .exp-payment-table .dpd-label {
    width: 38px;
    min-width: 38px;
  }

  .exp-payment-table .year-cell {
    color: var(--exp-blue);
    font-weight: 700;
    background: #fff;
  }

  .exp-first-payment-table {
    break-before: page;
    page-break-before: always;
  }

  .dpd-circle {
    display: inline-flex;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background: #15aa54;
    color: #fff;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9px;
    line-height: 1;
  }

  .dpd-circle.warning {
    background: #dc8a10;
  }

  .dpd-circle.danger {
    background: #bd3030;
  }

  .dpd-circle.info {
    background: #2f82c7;
  }

  .exp-personal {
    border-top: 1px solid var(--exp-border);
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .exp-personal-grid {
    display: grid;
    grid-template-columns: 21% 27% 27% 25%;
    border-top: 1px solid var(--exp-border);
    min-height: 42mm;
  }

  .exp-personal-cell {
    padding: 4px 6px 6px;
    border-right: 1px solid var(--exp-border);
    min-width: 0;
  }

  .exp-personal-cell:last-child {
    border-right: 0;
  }

  .exp-personal-row {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    column-gap: 5px;
    padding: 3px 0;
    font-size: 9px;
    line-height: 1.08;
  }

  .exp-personal-row .label,
  .exp-phone-head,
  .exp-id-table th {
    color: var(--exp-blue);
    font-weight: 700;
  }

  .exp-phone-table,
  .exp-id-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .exp-phone-table th,
  .exp-phone-table td,
  .exp-id-table th,
  .exp-id-table td {
    padding: 3px 3px;
    vertical-align: top;
    font-size: 9px;
    line-height: 1.05;
  }

  .exp-phone-table th {
    text-align: left;
  }

  .exp-id-table th {
    text-align: left;
  }

  .exp-id-table td {
    word-break: break-word;
  }

  .exp-code-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 5px;
  }

  .exp-code-table th,
  .exp-code-table td {
    border: 1px solid #e1e1e1;
    padding: 2px 4px;
    font-size: 9px;
    line-height: 1.07;
    vertical-align: top;
  }

  .exp-code-table th {
    color: var(--exp-blue);
    text-align: left;
    background: #fafafa;
    font-weight: 700;
  }

  .exp-summary-data {
    padding: 5px 7px 6px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 16px;
    row-gap: 0;
  }

  .exp-summary-data-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 6px;
    padding: 3px 0;
    border-bottom: 1px solid #ededed;
    font-size: 9px;
  }

  .exp-summary-data-row .label {
    font-weight: 700;
    color: #4e4e4e;
  }

  .exp-summary-data-row .value {
    text-align: right;
  }

  .exp-enquiry-card {
    border: 1px solid var(--exp-border);
    margin: 4px 6px 7px;
    padding: 5px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .exp-enquiry-top {
    display: grid;
    grid-template-columns: 78% 22%;
    color: var(--exp-blue);
    font-weight: 700;
  }

  .exp-enquiry-name {
    margin-bottom: 4px;
  }

  .exp-enquiry-grid {
    display: grid;
    grid-template-columns: 31% 34% 35%;
    margin-top: 8px;
  }

  .exp-enquiry-col {
    padding-right: 8px;
  }

  .exp-enquiry-row {
    display: grid;
    grid-template-columns: 46% 54%;
    gap: 5px;
    margin-bottom: 5px;
    font-size: 9px;
  }

  .exp-enquiry-row .label {
    color: var(--exp-blue);
    font-weight: 700;
  }

  .exp-static-text {
    padding: 8px 7px 9px;
    font-size: 9px;
    line-height: 1.22;
  }

  .exp-legend {
    padding: 6px 8px 8px;
    font-size: 9px;
  }

  .exp-legend-list {
    margin: 0;
    padding-left: 16px;
  }

  .exp-legend-list > li {
    padding: 1.7px 0;
  }

  .exp-legend-list > li::marker {
    color: var(--exp-blue);
  }

  .exp-legend-sublist {
    list-style: circle;
    margin: 1px 0 1px 17px;
    padding-left: 8px;
  }

  .exp-end-report {
    text-align: center;
    color: #9a9a9a;
    font-weight: 700;
    font-size: 10px;
    padding: 8px 0 4px;
    letter-spacing: .2px;
  }

  .exp-disclaimer {
    white-space: pre-line;
    color: #8a8a8a;
    font-size: 9px;
    line-height: 1.2;
    padding: 0 7px;
  }

  .page-counter::after {
    content: " " counter(page);
  }

  .exp-blue {
    color: var(--exp-blue);
  }

  .exp-nowrap {
    white-space: nowrap;
  }

  .exp-muted {
    color: #6b6b6b;
  }

  .exp-error-box {
    border: 1px solid #d78e8e;
    background: #fff4f4;
    color: #3d0000;
    margin-bottom: 6px;
    padding: 5px 7px;
    text-align: left;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11px;
  }

  .exp-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 12px;
  }

  .exp-action-btn {
    border: 0;
    border-radius: 4px;
    padding: 8px 15px;
    cursor: pointer;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 13px;
    color: white;
  }

  .exp-action-print { background: #2f8f3b; }
  .exp-action-close { background: #d34b4b; }

  .exp-page-break-before {
    break-before: page;
    page-break-before: always;
  }

  .exp-empty {
    padding: 12px;
    text-align: center;
    color: #777;
  }

  @media print {
    html,
    body {
      background: #fff;
      width: 210mm;
    }

    .experian-modal-scroll {
      width: auto;
      max-height: none;
      overflow: visible;
    }

    .experian-report {
      width: 210mm;
      min-height: 297mm;
      padding: 31mm 3.7mm 16mm;
      margin: 0;
      box-shadow: none;
    }

    .experian-print-header {
      position: fixed;
      top: 0;
      left: 3.7mm;
      right: 3.7mm;
      height: 27mm;
      background: #fff;
      z-index: 10;
    }

    .experian-print-footer {
      position: fixed;
      left: 3.7mm;
      right: 3.7mm;
      bottom: 0;
      height: 8mm;
      background: #fff;
      z-index: 10;
    }

    .exp-actions,
    .no-print {
      display: none !important;
    }

    .experian-section,
    .exp-account-block,
    .exp-enquiry-card,
    .exp-payment,
    .exp-personal {
      -webkit-box-shadow: none !important;
      box-shadow: none !important;
    }

    .experian-section-header,
    .experian-subheading,
    .exp-payment-title,
    .exp-personal-title {
      break-after: avoid;
      page-break-after: avoid;
    }

    .exp-account-heading,
    .exp-payment,
    .exp-personal,
    .exp-enquiry-card {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }

  @media screen and (max-width: 860px) {
    .experian-modal-scroll {
      width: 100%;
    }

    .experian-report {
      transform-origin: top center;
    }
  }
`;

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasValue(value) {
  if (value === 0 || value === false) return true;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (isObject(value)) return Object.keys(value).length > 0;
  return true;
}

function firstValue(object, paths, fallback = "-") {
  for (const path of paths) {
    const parts = String(path).split(".");
    let current = object;

    for (const part of parts) {
      if (current === null || current === undefined) break;
      current = current[part];
    }

    if (hasValue(current)) return current;
  }

  return fallback;
}

function firstObject(object, paths) {
  const value = firstValue(object, paths, null);
  if (Array.isArray(value)) return value[0] || {};
  return isObject(value) ? value : {};
}

function firstArray(object, paths) {
  for (const path of paths) {
    const parts = String(path).split(".");
    let current = object;

    for (const part of parts) {
      if (current === null || current === undefined) break;
      current = current[part];
    }

    if (Array.isArray(current) && current.length) return current;
  }

  return [];
}

function deepFindArrays(value, keyMatcher, result = []) {
  if (!value || result.length) return result;

  if (Array.isArray(value)) {
    for (const item of value) deepFindArrays(item, keyMatcher, result);
    return result;
  }

  if (!isObject(value)) return result;

  for (const [key, child] of Object.entries(value)) {
    if (keyMatcher(key) && Array.isArray(child) && child.length) {
      result.push(...child);
      return result;
    }
  }

  for (const child of Object.values(value)) {
    deepFindArrays(child, keyMatcher, result);
    if (result.length) return result;
  }

  return result;
}

function formatDate(value) {
  if (!hasValue(value)) return "-";

  const raw = String(value).trim();

  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(6, 8)}-${raw.slice(4, 6)}-${raw.slice(0, 4)}`;
  }

  if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split(/[-/]/);
    return `${day}-${month}-${year}`;
  }

  return raw;
}

function formatHeaderDate(value) {
  return formatDate(value);
}

function formatAmount(value) {
  if (!hasValue(value)) return "-";
  const numeric = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString("en-IN");
}

function formatInteger(value) {
  if (!hasValue(value)) return "-";
  const numeric = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString("en-IN");
}

function getFullName(source) {
  const direct = firstValue(source, [
    "Full_Name",
    "FullName",
    "Name",
    "Applicant_Name",
    "Customer_Name",
  ], "-");

  if (direct !== "-") return String(direct).trim();

  const names = [
    firstValue(source, ["First_Name", "FirstName"], ""),
    firstValue(source, ["Middle_Name", "MiddleName"], ""),
    firstValue(source, ["Last_Name", "LastName", "Surname"], ""),
  ].filter((value) => hasValue(value));

  return names.length ? names.join(" ").trim() : "-";
}

function getApplicantSources(report) {
  const currentApplication =
    report?.INProfileResponse?.Current_Application ||
    report?.Current_Application ||
    {};

  const details =
    currentApplication?.Current_Application_Details ||
    currentApplication ||
    {};

  return {
    application: currentApplication,
    details,
    applicant:
      details?.Current_Applicant_Details ||
      currentApplication?.Current_Applicant_Details ||
      {},
    other:
      details?.Current_Other_Details ||
      currentApplication?.Current_Other_Details ||
      {},
    addresses:
      details?.Current_Applicant_Address_Details ||
      currentApplication?.Current_Applicant_Address_Details ||
      [],
  };
}

function getApplicantName(report) {
  const { applicant } = getApplicantSources(report);
  return (
    getFullName(applicant) !== "-"
      ? getFullName(applicant)
      : hasValue(report?.name)
      ? String(report.name)
      : "-"
  );
}

function getApplicantAddress(report) {
  const { addresses } = getApplicantSources(report);
  const address = Array.isArray(addresses) ? addresses[0] : addresses;
  return buildAddress(address || {});
}

function buildAddress(address) {
  if (!address || !isObject(address)) return "-";

  const code = firstValue(address, ["State_Code", "StateCode", "State_Code_ID"], "");
  const state = firstValue(address, ["State", "State_Name"], "");
  const resolvedState = hasValue(state)
    ? String(state)
    : STATE_CODE_MAP[String(code)] || "";

  const parts = [
    firstValue(address, ["Address_1", "Address1", "Line1", "Address_Line1", "First_Line_Of_Address"], ""),
    firstValue(address, ["Address_2", "Address2", "Line2", "Address_Line2", "Second_Line_Of_Address"], ""),
    firstValue(address, ["Address_3", "Address3", "Line3", "Address_Line3", "Third_Line_Of_Address"], ""),
    firstValue(address, ["Locality", "Area", "Village", "City", "City_Name"], ""),
    firstValue(address, ["District", "District_Name"], ""),
    resolvedState,
    firstValue(address, ["Country", "Country_Name"], ""),
    firstValue(address, ["PIN", "Pin", "Pincode", "Pin_Code", "ZIP", "Zip_Code", "ZIP_Code"], ""),
  ]
    .map((value) => (hasValue(value) ? String(value).trim() : ""))
    .filter(Boolean);

  return parts.length ? parts.join(", ") : "-";
}

function getAccountTypeCode(account) {
  return String(
    firstValue(account, [
      "Account_Type",
      "AccountType",
      "Account_Type_Code",
      "AccountTypeCode",
    ], "00")
  );
}

function getAccountTypeName(account) {
  const code = getAccountTypeCode(account);
  return (
    ACCOUNT_TYPE_MAP[code] ||
    firstValue(account, ["Account_Type_Description", "AccountTypeDescription"], "Other")
  );
}

function getLenderName(account) {
  return firstValue(
    account,
    [
      "Subscriber_Name",
      "SubscriberName",
      "Member_Name",
      "MemberName",
      "Lender_Name",
      "LenderName",
      "Institution_Name",
      "InstitutionName",
      "Creditor_Name",
    ],
    "-"
  );
}

function getAccountHolder(account, report) {
  const { applicant } = getApplicantSources(report);

  return {
    ...applicant,
    ...firstObject(account, [
      "CAIS_Holder_Details",
      "CAIS_Holder_Detail",
      "Holder_Details",
      "Holder_Detail",
    ]),
  };
}

function getAccountAddress(account, report) {
  const address = firstObject(account, [
    "CAIS_Holder_Address_Details",
    "CAIS_Holder_Address_Detail",
    "Holder_Address_Details",
    "Holder_Address_Detail",
    "Address_Details",
    "Address_Detail",
  ]);

  return Object.keys(address).length ? buildAddress(address) : getApplicantAddress(report);
}

function getAccountHistory(account) {
  const direct = firstArray(account, [
    "CAIS_Account_History",
    "CAIS_Account_History_Details",
    "Account_History",
    "Payment_History",
    "PaymentHistory",
  ]);

  if (direct.length) return direct;

  const nestedHistory = deepFindArrays(
    account,
    (key) => /history|payment/i.test(key) && /array/i.test(String(Array))
  );

  return nestedHistory;
}

function normaliseMonth(value) {
  if (!hasValue(value)) return null;

  const raw = String(value).trim().toLowerCase();
  const monthNames = {
    january: 1,
    jan: 1,
    february: 2,
    feb: 2,
    march: 3,
    mar: 3,
    april: 4,
    apr: 4,
    may: 5,
    june: 6,
    jun: 6,
    july: 7,
    jul: 7,
    august: 8,
    aug: 8,
    september: 9,
    sep: 9,
    october: 10,
    oct: 10,
    november: 11,
    nov: 11,
    december: 12,
    dec: 12,
  };

  if (monthNames[raw]) return monthNames[raw];
  if (/^\d+$/.test(raw)) {
    const numeric = Number(raw);
    return numeric >= 1 && numeric <= 12 ? numeric : null;
  }

  return null;
}

function getHistoryMonth(item) {
  return normaliseMonth(
    firstValue(item, ["Month", "Month_Name", "month", "MONTH"], null)
  );
}

function getHistoryYear(item) {
  const value = firstValue(item, ["Year", "year", "YEAR"], null);
  if (!hasValue(value)) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : String(value);
}

function getHistoryDisplay(item) {
  const code = firstValue(
    item,
    ["Asset_Classification", "AssetClassification", "Classification", "Status"],
    ""
  );

  const dpd = firstValue(
    item,
    ["Days_Past_Due", "DaysPastDue", "DPD", "Days_Past_Due_Count"],
    ""
  );

  if (hasValue(code) && String(code).toUpperCase() !== "STD") {
    return String(code).trim();
  }

  if (hasValue(dpd)) return String(dpd);
  return "0";
}

function getHistoryCircleClass(display) {
  const raw = String(display).trim().toUpperCase();
  if (/^\d+$/.test(raw) && Number(raw) > 0) return "dpd-circle danger";
  if (["D", "DBT", "L", "LSS", "SUB", "B"].includes(raw)) return "dpd-circle danger";
  if (["P", "M"].includes(raw)) return "dpd-circle warning";
  if (["X", "N"].includes(raw)) return "dpd-circle info";
  return "dpd-circle";
}

function getPhones(source, account) {
  const arrayCandidates = [
    "CAIS_Holder_Phone_Details",
    "Holder_Phone_Details",
    "Phone_Details",
    "PhoneDetails",
    "Telephone_Details",
    "TelephoneDetails",
  ];

  let phones = firstArray(account, arrayCandidates);
  if (!phones.length) phones = firstArray(source, arrayCandidates);

  if (phones.length) {
    return phones.slice(0, 2).map((phone) => ({
      type: firstValue(phone, ["Phone_Type", "PhoneType", "Type"], "-"),
      number: firstValue(
        phone,
        ["Phone_Number", "PhoneNumber", "Mobile", "Telephone_Number", "Number"],
        "-"
      ),
      extension: firstValue(phone, ["Extension", "Phone_Extension"], "-"),
    }));
  }

  const phoneType = firstValue(source, ["Phone_Type", "PhoneType"], "Mobile");
  const phoneNumber = firstValue(
    source,
    [
      "MobilePhoneNumber",
      "Mobile_Phone_Number",
      "Mobile_Number",
      "Phone_Number",
      "PhoneNumber",
      "Telephone_Number",
    ],
    "-"
  );

  return [
    {
      type: phoneType,
      number: phoneNumber,
      extension: firstValue(source, ["Extension", "Phone_Extension"], "-"),
    },
  ];
}

function getIdValue(holder, report, paths) {
  const { applicant } = getApplicantSources(report);
  return firstValue(holder, paths, firstValue(applicant, paths, "-"));
}

function getAccountPersonalDetails(account, report) {
  const holder = getAccountHolder(account, report);
  const phones = getPhones(holder, account);

  return {
    dob: formatDate(
      firstValue(holder, [
        "Date_of_Birth",
        "Date_Of_Birth",
        "DOB",
        "DateOfBirth",
        "Birth_Date",
      ], "-")
    ),
    gender: (() => {
      const rawGender = firstValue(holder, ["Gender", "Gender_Description"], null);
      if (hasValue(rawGender)) return rawGender;
      return ({ "1": "Male", "2": "Female", "3": "Other" }[
        String(firstValue(holder, ["Gender_Code", "GenderCode"], ""))
      ] || "-");
    })(),
    occupation: firstValue(holder, ["Occupation", "Occupation_Description"], "-"),
    email: firstValue(
      holder,
      ["Email", "EmailAddress", "Email_Address", "Email_ID"],
      "-"
    ),
    phones,
    ids: {
      PAN: getIdValue(holder, report, ["PAN", "Pan", "IncomeTaxNumber"]),
      Passport: getIdValue(holder, report, ["Passport", "Passport_Number"]),
      "Voter ID": getIdValue(holder, report, ["Voter_ID", "VoterID"]),
      "Aadhaar/UID": getIdValue(holder, report, ["Aadhaar", "Aadhaar_Number", "Aadhaar_UID"]),
      "Driving License": getIdValue(holder, report, ["Driving_License", "DrivingLicense", "Driver_License_Number"]),
      "Ration Card": getIdValue(holder, report, ["Ration_Card", "RationCard"]),
    },
  };
}

function getAccountFields(account) {
  const accountType = getAccountTypeName(account);
  const value = (paths, fallback = "-") => firstValue(account, paths, fallback);
  const sanctionedAmount = value(["Sanctioned_Amount", "Sanction_Amount", "Amount_Sanctioned"], null);
  const highestCredit = value(["Highest_Credit", "HighestCredit", "High_Credit"], null);
  const creditLabel = hasValue(sanctionedAmount) ? "Sanctioned Amt" : "Highest Credit";
  const creditValue = hasValue(sanctionedAmount) ? sanctionedAmount : highestCredit;

  return [
    {
      group: "Account terms",
      rows: [
        ["Account Number", value(["Account_Number", "AccountNo", "Account_No", "AccountNumber"])],
        ["Date Opened", formatDate(value(["Date_Opened", "DateOpened", "Open_Date", "Opening_Date"]))],
        ["Date Closed", formatDate(value(["Date_Closed", "DateClosed", "Close_Date", "Closing_Date"]))],
        ["Ownership", value(["Ownership", "Ownership_Type", "OwnershipType"])],
        ["Rate of Interest", value(["Rate_of_Interest", "RateOfInterest", "Interest_Rate"])],
        ["Value of Collateral", formatAmount(value(["Value_of_Collateral", "Collateral_Value"]))],
        ["Type of Collateral", value(["Type_of_Collateral", "Collateral_Type"])],
      ],
    },
    {
      group: "Account description",
      rows: [
        ["Date Reported", formatDate(value(["Date_Reported", "DateReported", "Reported_Date"]))],
        ["Loan Type", accountType],
        ["Account Status", value(["Account_Status", "AccountStatus", "Status"])],
        [creditLabel, formatAmount(creditValue)],
        ["Current Balance", formatAmount(value(["Current_Balance", "CurrentBalance", "Balance"]))],
        ["Amount Overdue", formatAmount(value(["Amount_Overdue", "Amount_Past_Due", "Past_Due_Amount"]))],
        ["Last Payment Date", formatDate(value(["Last_Payment_Date", "LastPaymentDate"]))],
        ["SuitFiled Willful Default", value(["SuitFiled_Willful_Default", "SuitFiledWillfulDefault", "Suit_Filed_Willful_Default"])],
      ],
    },
    {
      group: "Account details",
      rows: [
        ["Credit Limit Amt", formatAmount(value(["Credit_Limit_Amount", "Credit_Limit_Amt", "CreditLimitAmount"]))],
        ["EMI", formatAmount(value(["EMI", "EMI_Amount", "EMIAmount"]))],
        ["Repayment Tenure", value(["Repayment_Tenure", "RepaymentTenure", "Tenure"])],
        ["Total Write-off Amt", formatAmount(value(["Total_Write_Off_Amount", "Total_Writeoff_Amount", "Write_Off_Amount"]))],
        ["Principal Write-off", formatAmount(value(["Principal_Write_Off", "Principal_Writeoff", "Principal_Write_Off_Amount"]))],
        ["Settlement Amt", formatAmount(value(["Settlement_Amount", "SettlementAmt"]))],
        ["Credit Facility Status", value(["Credit_Facility_Status", "CreditFacilityStatus"])],
      ],
    },
  ];
}

function getEnquiries(report) {
  const exact = firstArray(report, [
    "Credit_Enquiries",
    "Credit_Enquiry",
    "CreditEnquiries",
    "CreditEnquiry",
    "Enquiry_Details",
    "Enquiries",
    "Enquiry",
    "INProfileResponse.Credit_Enquiries",
    "INProfileResponse.Credit_Enquiry",
    "INProfileResponse.Enquiry_Details",
  ]);

  if (exact.length) return exact;

  return deepFindArrays(report, (key) => /enquir|inquir/i.test(key));
}

function getEnquiryName(enquiry, report) {
  const direct = firstValue(enquiry, ["Name", "Consumer_Name", "Applicant_Name"], "-");
  return direct !== "-" ? direct : getApplicantName(report);
}

function getEnquiryAddress(enquiry, report) {
  const direct = firstValue(enquiry, ["Address_1", "Address1", "Address"], null);
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  const address = firstObject(enquiry, ["Address_Details", "Applicant_Address"]);
  return Object.keys(address).length ? buildAddress(address) : getApplicantAddress(report);
}

function getEnquiryValue(enquiry, paths, fallback = "-") {
  return firstValue(enquiry, paths, fallback);
}

function getEnquiryRecordsFound(report, enquiries) {
  const count = firstValue(report, [
    "Credit_Enquiry_Records_Found",
    "CreditEnquiryRecordsFound",
    "Enquiry_Records_Found",
    "Records_Found",
  ], null);

  return hasValue(count) ? String(count) : String(enquiries.length);
}

function getReportHeader(report) {
  const header =
    report?.INProfileResponse?.CreditProfileHeader ||
    report?.CreditProfileHeader ||
    {};

  return {
    ern: firstValue(
      header,
      [
        "ExperianReportNumber",
        "Experian_Report_Number",
        "ReportNumber",
        "ERN",
      ],
      firstValue(report, ["ExperianReportNumber", "ReportNumber", "ERN"], "-")
    ),
    transactionId: firstValue(
      header,
      [
        "UniqueTransactionID",
        "Unique_Transaction_ID",
        "TransactionID",
        "UniqueTransactionId",
      ],
      firstValue(report, ["UniqueTransactionID", "TransactionID"], "-")
    ),
    created: formatHeaderDate(
      firstValue(
        header,
        ["ReportCreated", "Report_Created", "ReportDate", "Report_Date", "CreatedDate"],
        firstValue(report, ["ReportCreated", "ReportDate"], "-")
      )
    ),
  };
}

function getCurrentApplicationInfo(report) {
  const { applicant, other } = getApplicantSources(report);
  const fallbackName = getApplicantName(report);

  return {
    name:
      getFullName(applicant) !== "-"
        ? getFullName(applicant)
        : fallbackName,
    mobile: firstValue(
      applicant,
      [
        "MobilePhoneNumber",
        "Mobile_Phone_Number",
        "Mobile_Number",
        "Phone_Number",
        "PhoneNumber",
      ],
      firstValue(report, ["mobile", "Mobile"], "-")
    ),
    telephone: firstValue(
      other,
      ["Telephone", "Telephone_Number", "TelephoneNumber", "Telephone_No"],
      firstValue(applicant, ["Telephone", "Telephone_Number", "TelephoneNumber"], "-")
    ),
    passport: firstValue(applicant, ["Passport_Number", "PassportNumber", "Passport"], "-"),
    pan: firstValue(
      applicant,
      ["IncomeTaxNumber", "PAN", "Pan"],
      firstValue(report, ["pan", "PAN", "Pan"], "-")
    ),
    email: firstValue(applicant, ["Email", "EmailAddress", "Email_Address"], "-"),
    voterId: firstValue(applicant, ["Voter_ID", "VoterID"], "-"),
    drivingLicense: firstValue(
      applicant,
      ["Driver_License_Number", "Driving_License", "DrivingLicense"],
      "-"
    ),
    aadhaar: firstValue(applicant, ["Aadhaar_Number", "Aadhaar", "Aadhaar_UID"], "-"),
    rationCard: firstValue(applicant, ["Ration_Card", "RationCard"], "-"),
  };
}

function getSummaryMetrics(report, accounts) {
  const summary = report?.CAIS_Account?.CAIS_Summary || {};
  const active = accounts.filter((account) => {
    const status = String(
      firstValue(account, ["Account_Status", "AccountStatus", "Status"], "")
    ).toUpperCase();
    return status === "ACTIVE";
  }).length;

  const closed = accounts.filter((account) => {
    const status = String(
      firstValue(account, ["Account_Status", "AccountStatus", "Status"], "")
    ).toUpperCase();
    return status === "CLOSED";
  }).length;

  const totalBalance = accounts.reduce((sum, account) => {
    const value = Number(
      String(firstValue(account, ["Current_Balance", "CurrentBalance", "Balance"], 0)).replace(/,/g, "")
    );
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  const overdue = accounts.reduce((sum, account) => {
    const value = Number(
      String(
        firstValue(account, ["Amount_Overdue", "Amount_Past_Due", "Past_Due_Amount"], 0)
      ).replace(/,/g, "")
    );
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  const secured = accounts.reduce((sum, account) => {
    const typeCode = getAccountTypeCode(account);
    const securedCodes = new Set(["01", "02", "03", "04", "07", "13", "31", "32", "33", "34"]);
    if (!securedCodes.has(typeCode)) return sum;
    const value = Number(
      String(firstValue(account, ["Current_Balance", "CurrentBalance", "Balance"], 0)).replace(/,/g, "")
    );
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  const writtenOff = accounts.reduce((sum, account) => {
    const values = [
      firstValue(account, ["Total_Write_Off_Amount", "Total_Writeoff_Amount", "Write_Off_Amount"], 0),
      firstValue(account, ["Settlement_Amount", "SettlementAmt"], 0),
      firstValue(account, ["Amount_Written_Off", "Written_Off_Amount"], 0),
    ];
    return (
      sum +
      values.reduce((rowSum, value) => {
        const numeric = Number(String(value).replace(/,/g, ""));
        return Number.isFinite(numeric) ? rowSum + numeric : rowSum;
      }, 0)
    );
  }, 0);

  const enquiries = getEnquiries(report);

  return {
    totalAccounts: firstValue(summary, ["Total_Accounts", "Total_Account", "TotalAccounts"], accounts.length),
    activeAccounts: firstValue(summary, ["Active_Accounts", "ActiveAccounts"], active),
    closedAccounts: firstValue(summary, ["Closed_Accounts", "ClosedAccounts"], closed),
    ccCo: firstValue(summary, ["CC_CO", "CC_CO_Count", "CC_CO_Accounts"], "-"),
    currentBalance: firstValue(
      summary,
      ["Total_Current_Balance", "Current_Balance", "Total_Current_Balance_Amount"],
      totalBalance
    ),
    sfWdWoSettled: firstValue(
      summary,
      [
        "SF_WD_WO_Settled",
        "SF_WD_WO_Settled_Amount",
        "Write_Off_Settlement_Amount",
      ],
      writtenOff
    ),
    securedAccounts: firstValue(summary, ["Secured_Accounts", "Secured_Accounts_Amount"], secured),
    unsecuredAccounts: firstValue(
      summary,
      ["Unsecured_Accounts", "Unsecured_Accounts_Amount"],
      Math.max(Number(totalBalance) - Number(secured || 0), 0)
    ),
    enquiryCount: enquiries.length,
    overdue,
  };
}

function Section({ title, right, children, description }) {
  return (
    <section className="experian-section">
      <div className="experian-section-header">
        <span className="experian-section-check">✓</span>
        <span className="experian-section-title">{title}</span>
        {right ? <span className="experian-section-right">{right}</span> : null}
      </div>
      {description ? <div className="experian-note">{description}</div> : null}
      {children}
    </section>
  );
}

function KV({ label, value, labelClass = "" }) {
  return (
    <>
      <td className={`label ${labelClass}`}>{label}</td>
      <td className="value">{hasValue(value) ? value : "-"}</td>
    </>
  );
}

function ScoreSection({ score }) {
  return (
    <Section title="EXPERIAN CREDIT SCORE">
      <div className="exp-score-wrap">
        <div className="exp-score-intro">
          Your Experian Credit Report is summarized in the form of Experian Credit Score which ranges from 300 - 900.
        </div>
        <div className="exp-score-layout">
          <div className="exp-score-box">
            <div className="caption">YOUR EXPERIAN<br />CREDIT SCORE</div>
            <div className="number">{hasValue(score) ? score : "-"}</div>
            <div className="range">Range: 300 - 900</div>
          </div>
          <div className="exp-factors">
            <div className="exp-factors-title">Score Factors</div>
            {CREDIT_SCORE_FACTORS.map(([label, text], index) => (
              <div className="exp-factor-row" key={label}>
                <span>{index + 1}.</span>
                <span className="exp-factor-label">{label} :</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function CurrentApplicationSection({ report }) {
  const info = getCurrentApplicationInfo(report);

  return (
    <Section
      title="CURRENT APPLICATION INFORMATION"
      description={SECTION_DESCRIPTION.currentApplication}
    >
      <div className="exp-current-info">
        <table className="exp-kv-table">
          <colgroup>
            <col style={{ width: "12.5%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "22.5%" }} />
          </colgroup>
          <tbody>
            <tr>
              <KV label="Name" value={info.name} />
              <td colSpan={4} />
            </tr>
            <tr>
              <KV label="Mobile Phone" value={info.mobile} />
              <KV label="Telephone" value={info.telephone} />
              <td colSpan={2} />
            </tr>
            <tr>
              <KV label="Passport Number" value={info.passport} />
              <KV label="PAN" value={info.pan} />
              <td colSpan={2} />
            </tr>
            <tr>
              <KV label="Email" value={info.email} />
              <KV label="Voter ID" value={info.voterId} />
              <KV label="Driving License" value={info.drivingLicense} />
            </tr>
            <tr>
              <td className="label" />
              <td className="value" />
              <KV label="Aadhaar Number" value={info.aadhaar} />
              <KV label="Ration Card" value={info.rationCard} />
            </tr>
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function ReportSummarySection({ report, accounts }) {
  const metrics = getSummaryMetrics(report, accounts);

  return (
    <Section title="REPORT SUMMARY">
      <div className="exp-summary-grid">
        <div className="exp-summary-col">
          <div className="exp-summary-title">Credit Account Summary</div>
          <div className="exp-summary-row">
            <span className="label">Total number of Accounts</span>
            <span className="value">{formatInteger(metrics.totalAccounts)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">Active Accounts</span>
            <span className="value">{formatInteger(metrics.activeAccounts)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">Closed Accounts</span>
            <span className="value">{formatInteger(metrics.closedAccounts)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">CC/CO</span>
            <span className="value">{metrics.ccCo}</span>
          </div>
        </div>

        <div className="exp-summary-col">
          <div className="exp-summary-title">Current Balance Amount Summary</div>
          <div className="exp-summary-row">
            <span className="label">Total Current Bal. amt</span>
            <span className="value">{formatAmount(metrics.currentBalance)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">SF/WD/WO/Settled amt</span>
            <span className="value">{formatAmount(metrics.sfWdWoSettled)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">Secured Accounts amt</span>
            <span className="value">{formatAmount(metrics.securedAccounts)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">Unsecured Accounts amt</span>
            <span className="value">{formatAmount(metrics.unsecuredAccounts)}</span>
          </div>
        </div>

        <div className="exp-summary-col">
          <div className="exp-summary-title">Credit Enquiry Summary</div>
          <div className="exp-summary-row">
            <span className="label">Last 7 days credit enquiries</span>
            <span className="value">{firstValue(report, ["EnquirySummary.Last_7_Days", "Last_7_Days_Enquiries"], 0)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">Last 30 days credit enquiries</span>
            <span className="value">{firstValue(report, ["EnquirySummary.Last_30_Days", "Last_30_Days_Enquiries"], 0)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">Last 90 days credit enquiries</span>
            <span className="value">{firstValue(report, ["EnquirySummary.Last_90_Days", "Last_90_Days_Enquiries"], 0)}</span>
          </div>
          <div className="exp-summary-row">
            <span className="label">Last 180 days credit enquiries</span>
            <span className="value">{firstValue(report, ["EnquirySummary.Last_180_Days", "Last_180_Days_Enquiries"], 0)}</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

function AccountSummarySection({ accounts }) {
  return (
    <div className="exp-page-break-before">
    <Section
      title="SUMMARY: CREDIT ACCOUNT INFORMATION"
      description={SECTION_DESCRIPTION.accountSummary}
    >
      {accounts.length === 0 ? (
        <div className="exp-empty">No credit account information available.</div>
      ) : (
        <div className="exp-account-summary">
          <table>
            <colgroup>
              <col style={{ width: "4%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "4%" }} />
              <col style={{ width: "4%" }} />
            </colgroup>
            <thead>
              <tr>
                <th colSpan={2}>Lender</th>
                <th>Account type</th>
                <th>Account No</th>
                <th>Ownership</th>
                <th>Date Reported</th>
                <th>Account Status</th>
                <th>Date Opened</th>
                <th>Sanction Amt /<br />Highest Credit</th>
                <th>Current Balance</th>
                <th>Amount Overdue</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account, index) => {
                const lender = getLenderName(account);
                const type = getAccountTypeName(account);
                const accountNo = firstValue(account, [
                  "Account_Number",
                  "AccountNo",
                  "Account_No",
                  "AccountNumber",
                ], "-");
                const ownership = firstValue(account, ["Ownership", "Ownership_Type", "OwnershipType"], "-");
                const status = firstValue(account, ["Account_Status", "AccountStatus", "Status"], "-");
                const reportDate = formatDate(firstValue(account, ["Date_Reported", "DateReported", "Reported_Date"], "-"));
                const opened = formatDate(firstValue(account, ["Date_Opened", "DateOpened", "Open_Date", "Opening_Date"], "-"));
                const sanctioned = firstValue(account, ["Sanctioned_Amount", "Sanction_Amount", "Amount_Sanctioned"], null);
                const highest = firstValue(account, ["Highest_Credit", "HighestCredit", "High_Credit"], null);
                const current = firstValue(account, ["Current_Balance", "CurrentBalance", "Balance"], 0);
                const overdue = firstValue(account, ["Amount_Overdue", "Amount_Past_Due", "Past_Due_Amount"], "-");

                return (
                  <tr key={`${accountNo}-${index}`}>
                    <td className="serial">Acct<br />{index + 1}</td>
                    <td>{lender}</td>
                    <td>{type}</td>
                    <td>{accountNo}</td>
                    <td>{ownership}</td>
                    <td>{reportDate}</td>
                    <td>{status}</td>
                    <td>{String(firstValue(account, ["Date_Opened", "DateOpened", "Open_Date", "Opening_Date"], "-"))}</td>
                    <td>{formatAmount(hasValue(sanctioned) ? sanctioned : highest)}</td>
                    <td>{formatAmount(current)}</td>
                    <td>{formatAmount(overdue)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
    </div>
  );
}

function PaymentHistoryTable({ account, forcePageBreak = false }) {
  const history = getAccountHistory(account);
  const grouped = new Map();

  history.forEach((item) => {
    const year = getHistoryYear(item);
    const month = getHistoryMonth(item);
    if (year === null || month === null) return;

    if (!grouped.has(String(year))) grouped.set(String(year), {});
    grouped.get(String(year))[month] = item;
  });

  const years = Array.from(grouped.keys()).sort((a, b) => Number(b) - Number(a));

  return (
    <div className="exp-payment">
      <div className="exp-payment-title">Payment History</div>
      <table className={`exp-payment-table${forcePageBreak ? " exp-first-payment-table" : ""}`}>
        <thead>
          <tr>
            <th className="dpd-label">DPD</th>
            {MONTHS.map((month) => (
              <th key={month.number}>{month.short}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {years.length ? (
            years.map((year) => (
              <tr key={year}>
                <td className="year-cell">{year}</td>
                {MONTHS.map((month) => {
                  const item = grouped.get(String(year))?.[month.number];
                  if (!item) return <td key={month.number} />;

                  const display = getHistoryDisplay(item);
                  return (
                    <td key={month.number}>
                      <span className={getHistoryCircleClass(display)}>{display}</span>
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td className="year-cell">-</td>
              {MONTHS.map((month) => <td key={month.number} />)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function PersonalDetailsTable({ account, report }) {
  const data = getAccountPersonalDetails(account, report);
  const phones = [...data.phones];
  while (phones.length < 2) {
    phones.push({ type: "-", number: "-", extension: "-" });
  }

  const idRows = Object.entries(data.ids);

  return (
    <div className="exp-personal">
      <div className="exp-personal-title">Consumer Personal details on the Credit Account</div>
      <div className="exp-personal-grid">
        <div className="exp-personal-cell">
          <div className="exp-personal-row"><span className="label">Date of Birth</span><span>{data.dob}</span></div>
          <div className="exp-personal-row"><span className="label">Gender</span><span>{data.gender}</span></div>
          <div className="exp-personal-row"><span className="label">Occupation</span><span>{data.occupation}</span></div>
          <div className="exp-personal-row"><span className="label">Email address</span><span style={{ wordBreak: "break-word" }}>{data.email}</span></div>
        </div>

        <div className="exp-personal-cell">
          <table className="exp-phone-table">
            <thead>
              <tr>
                <th className="exp-phone-head">Phone Type</th>
                <th className="exp-phone-head">Phone Number</th>
                <th className="exp-phone-head">Extension</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{phones[0].type}</td>
                <td>{phones[0].number}</td>
                <td>{phones[0].extension}</td>
              </tr>
              <tr>
                <td>{phones[1].type}</td>
                <td>{phones[1].number}</td>
                <td>{phones[1].extension}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="exp-personal-cell">
          <table className="exp-phone-table">
            <thead>
              <tr>
                <th className="exp-phone-head">Phone Type</th>
                <th className="exp-phone-head">Phone Number</th>
                <th className="exp-phone-head">Extension</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{phones[0].type}</td>
                <td>{phones[0].number}</td>
                <td>{phones[0].extension}</td>
              </tr>
              <tr>
                <td>{phones[1].type}</td>
                <td>{phones[1].number}</td>
                <td>{phones[1].extension}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="exp-personal-cell">
          <table className="exp-id-table">
            <thead>
              <tr>
                <th>ID Type</th>
                <th>ID Number</th>
                <th>Date of issue</th>
                <th>Date of Expiry</th>
              </tr>
            </thead>
            <tbody>
              {idRows.map(([label, value]) => (
                <tr key={label}>
                  <td className="exp-blue">{label}</td>
                  <td>{value}</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AccountDetails({ account, index, report }) {
  const accountType = getAccountTypeName(account);
  const lender = getLenderName(account);
  const holder = getFullName(getAccountHolder(account, report));
  const address = getAccountAddress(account, report);
  const fields = getAccountFields(account);

  return (
    <article className="exp-account-block">
      <div className="exp-account-heading">
        <div className="exp-account-topline">
          <div>{accountType}</div>
          <div>{lender}</div>
          <div className="exp-account-act">
            <span className="circle">✓</span>
            <span>Acct {index + 1}</span>
          </div>
        </div>
        <div className="exp-account-underline" />
        <div className="exp-account-name">{holder}</div>
        <div className="exp-account-address">
          <span className="label">Address 1</span>
          <span className="value">{address}</span>
        </div>
      </div>

      <div className="exp-subheading">Credit Account details</div>
      <table className="exp-terms-table">
        <thead>
          <tr>
            <th>Account terms</th>
            <th>Account description</th>
            <th>Account details</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {fields.map((group) => (
              <td key={group.group}>
                {group.rows.map(([label, value]) => (
                  <div className="exp-term-row" key={label}>
                    <span className="exp-term-label">{label}</span>
                    <span className="exp-term-value">{hasValue(value) ? value : "-"}</span>
                  </div>
                ))}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <PaymentHistoryTable account={account} forcePageBreak={index === 0} />
      <PersonalDetailsTable account={account} report={report} />
    </article>
  );
}

function AccountDetailsSection({ report, accounts }) {
  return (
    <Section
      title="CREDIT ACCOUNT INFORMATION DETAILS"
      description={SECTION_DESCRIPTION.accountDetails}
    >
      <div>
        {accounts.length ? (
          accounts.map((account, index) => (
            <AccountDetails
              key={`${getAccountTypeCode(account)}-${index}`}
              account={account}
              index={index}
              report={report}
            />
          ))
        ) : (
          <div className="exp-empty">No credit account information details available.</div>
        )}
      </div>
    </Section>
  );
}

function AccountCodeTable() {
  const entries = Object.entries(ACCOUNT_TYPE_MAP);
  const rows = [];
  for (let i = 0; i < entries.length; i += 2) {
    rows.push([entries[i], entries[i + 1]]);
  }

  return (
    <div className="exp-section" style={{ padding: 0 }}>
      <table className="exp-code-table">
        <thead>
          <tr>
            <th style={{ width: "8%" }}>Code</th>
            <th>Account Type</th>
            <th style={{ width: "8%" }}>Code</th>
            <th>Account Type</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([left, right], index) => (
            <tr key={index}>
              <td>{left?.[0] || ""}</td>
              <td>{left?.[1] || ""}</td>
              <td>{right?.[0] || ""}</td>
              <td>{right?.[1] || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RawSummarySection({ report }) {
  const summary = report?.CAIS_Account?.CAIS_Summary;
  if (!summary || !isObject(summary)) return null;

  const rows = [];
  const walk = (obj, prefix = "") => {
    Object.entries(obj).forEach(([key, value]) => {
      if (!hasValue(value)) return;
      const nextLabel = prefix ? `${prefix} ${key}` : key;
      if (isObject(value)) {
        const allPrimitive = Object.values(value).every((item) => !isObject(item) && !Array.isArray(item));
        if (allPrimitive) {
          Object.entries(value).forEach(([childKey, childValue]) => {
            if (hasValue(childValue)) rows.push([childKey, childValue]);
          });
        } else {
          walk(value, nextLabel);
        }
      } else if (!Array.isArray(value)) {
        rows.push([nextLabel, value]);
      }
    });
  };

  walk(summary);
  if (!rows.length) return null;

  const labelise = (key) =>
    String(key)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\s+/g, " ")
      .trim();

  return (
    <Section title="SUMMARY">
      <div className="exp-summary-data">
        {rows.map(([key, value], index) => (
          <div className="exp-summary-data-row" key={`${key}-${index}`}>
            <span className="label">{labelise(key)}</span>
            <span className="value">{typeof value === "number" ? value.toLocaleString("en-IN") : String(value)}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

function EnquiryCard({ enquiry, index, report }) {
  const name = getEnquiryName(enquiry, report);
  const address = getEnquiryAddress(enquiry, report);

  const row = (label, paths) => {
    let value = getEnquiryValue(enquiry, paths, "-");
    if (label === "Date of Birth" || label === "Application date") value = formatDate(value);
    if (label === "Amount applied for") value = formatAmount(value);

    return (
      <div className="exp-enquiry-row" key={label}>
        <span className="label">{label}</span>
        <span>{value}</span>
      </div>
    );
  };

  return (
    <div className="exp-enquiry-card">
      <div className="exp-enquiry-top">
        <div>
          <div className="exp-enquiry-name">{name}</div>
          <div>Address 1</div>
          <div style={{ color: "#202020", fontWeight: 400, marginTop: 5 }}>{address}</div>
        </div>
        <div className="exp-account-act" style={{ alignItems: "flex-start", paddingTop: 1 }}>
          <span className="circle">✓</span>
          <span>Cr Enq {index + 1}</span>
        </div>
      </div>

      <div className="exp-enquiry-grid">
        <div className="exp-enquiry-col">
          {row("Date of Birth", ["Date_of_Birth", "DOB", "DateOfBirth"])}
          {row("Telephone", ["Telephone", "Telephone_Number", "TelephoneNumber"])}
          {row("Mobile Phone", ["Mobile_Phone", "MobilePhone", "Mobile_Number"])}
          {row("Gender", ["Gender", "Gender_Description"])}
          {row("Marital Status", ["Marital_Status", "MaritalStatus"])}
          {row("Email", ["Email", "EmailAddress"])}
        </div>
        <div className="exp-enquiry-col">
          {row("PAN", ["PAN", "Pan", "IncomeTaxNumber"])}
          {row("Passport Number", ["Passport_Number", "Passport"])}
          {row("Voter ID", ["Voter_ID", "VoterID"])}
          {row("Driving License", ["Driving_License", "DrivingLicense", "Driver_License_Number"])}
          {row("Ration Card", ["Ration_Card", "RationCard"])}
        </div>
        <div className="exp-enquiry-col">
          {row("ERN", ["ERN", "Enquiry_Reference_Number", "EnquiryReferenceNumber"])}
          {row("Search Type", ["Search_Type", "SearchType"])}
          {row("Credit Institution Name", ["Credit_Institution_Name", "Institution_Name", "Lender_Name"])}
          {row("Application date", ["Application_Date", "ApplicationDate"])}
          {row("Amount applied for", ["Amount_Applied_For", "AmountAppliedFor", "Amount_Applied"])}
          {row("Duration of Agreement", ["Duration_of_Agreement", "DurationOfAgreement", "Agreement_Duration"])}
        </div>
      </div>
    </div>
  );
}

function EnquirySection({ report }) {
  const enquiries = getEnquiries(report);

  return (
    <div className="exp-page-break-before">
    <Section
      title="CREDIT ENQUIRIES"
      right={`Records Found: ${getEnquiryRecordsFound(report, enquiries)}`}
      description={SECTION_DESCRIPTION.enquiries}
    >
      {enquiries.length ? (
        enquiries.map((enquiry, index) => (
          <EnquiryCard enquiry={enquiry} index={index} report={report} key={index} />
        ))
      ) : (
        <div className="exp-empty">No credit enquiries available.</div>
      )}
    </Section>
    </div>
  );
}

function ContactingUsSection() {
  return (
    <Section title="CONTACTING US">
      <div className="exp-static-text">
        If you find a discrepancy in your report, it is important to quickly raise it with your lender.Under the Credit Information Companies (Regulation) Act 2005 and as per the guidelines set by the Reserve Bank of India, Experian Credit Information Company of India Private Limited is not authorized to change any data in your credit information report without authorisation from your lender. However, we would be happy to help you with this process. If you would like us to help please visit https://consumer.experian.in/ECV-OLN/ to raise a dispute regarding the discrepancy.
      </div>
    </Section>
  );
}

function LegendSection() {
  return (
    <Section title="LEGEND">
      <div className="exp-legend">
        <ul className="exp-legend-list">
          {LEGEND_ITEMS.map(([label, text]) => (
            <li key={label}>
              <em>
                <strong>{label}</strong>: {text}
              </em>
              {label === "DPD >0" ? null : null}
            </li>
          ))}
          <li>
            <em>
              <strong>When the below alphabets are shown in the DPD table</strong>, it means the lender is reporting Asset Classification and indicates the following:
            </em>
            <ul className="exp-legend-sublist">
              {ASSET_CLASSIFICATION_ITEMS.map(([label, text]) => (
                <li key={label}>
                  <em><strong>{label}</strong> : {text}</em>
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </div>
    </Section>
  );
}

function ReportHeader({ report }) {
  const meta = getReportHeader(report);
  return (
    <div className="experian-print-header">
      <img src={EXPERIAN_LOGO_URL} alt="Experian" />
      <div className="experian-print-title">Experian Credit Report</div>
      <div className="experian-report-meta">
        <div>Experian Report Number (ERN): <strong>{meta.ern}</strong></div>
        <div>Unique Transaction ID: <strong>{meta.transactionId}</strong></div>
        <div>Report Created: <strong>{meta.created}</strong></div>
      </div>
    </div>
  );
}

function ReportFooter() {
  return (
    <div className="experian-print-footer">
      <span>© Experian Ltd, 2026. All rights reserved.</span>
      <span className="page-counter">Page</span>
    </div>
  );
}

function ReportDocument({ report }) {
  const accounts = useMemo(() => {
    const direct = report?.CAIS_Account?.CAIS_Account_DETAILS;
    if (Array.isArray(direct)) return direct;

    const nested = direct?.CAIS_Account_DETAILS || report?.CAIS_Account?.CAIS_Account_DETAILS?.CAIS_Account_DETAILS;
    return Array.isArray(nested) ? nested : [];
  }, [report]);

  const score =
    report?.SCORE?.BureauScore ||
    report?.SCORE?.Score ||
    report?.SCORE?.CreditScore ||
    report?.credit_score ||
    "-";

  return (
    <div className="experian-report" id="experian-report-document">
      <ReportHeader report={report} />

      <div className="experian-page-content">
        <CurrentApplicationSection report={report} />
        <ScoreSection score={score} />
        <ReportSummarySection report={report} accounts={accounts} />

        <AccountSummarySection accounts={accounts} />
        <AccountDetailsSection report={report} accounts={accounts} />
        <EnquirySection report={report} />
        <ContactingUsSection />
        <LegendSection />

        <div className="exp-end-report">&lt;---------- END OF REPORT ----------&gt;</div>
        <div className="exp-disclaimer">{REPORT_DISCLAIMER}</div>
      </div>

      <ReportFooter />
    </div>
  );
}

function getReportErrorMessage(ExperianReport, Equifaxv2Error, reportMessage) {
  return (
    Equifaxv2Error?.error?.credit_report_message?.message ||
    Equifaxv2Error?.message ||
    ExperianReport?.message ||
    reportMessage ||
    ""
  );
}

function getReportErrorCode(Equifaxv2Error, ExperianReport, reportCode) {
  return (
    Equifaxv2Error?.error?.credit_report_message?.message_code ||
    Equifaxv2Error?.message_code ||
    ExperianReport?.message_code ||
    reportCode ||
    ""
  );
}

const ExperianScoreReportModal = ({
  isOpen,
  onClose,
  ExperianReport,
  Equifaxv2Error,
  displayMessageBlock = true,
  reportMessage = "",
  reportCode = "",
}) => {
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("experian-report-modal-open");
    return () => document.body.classList.remove("experian-report-modal-open");
  }, [isOpen]);

  if (!isOpen) return null;

  const errorMessage = getReportErrorMessage(
    ExperianReport,
    Equifaxv2Error,
    reportMessage
  );
  const errorCode = getReportErrorCode(
    Equifaxv2Error,
    ExperianReport,
    reportCode
  );

  const showError =
    displayMessageBlock &&
    (hasValue(errorMessage) || hasValue(errorCode)) &&
    !String(errorMessage).toLowerCase().includes("success");

  const printReport = () => {
    setIsPrinting(true);

    const source = document.getElementById("experian-report-document");
    if (!source) {
      setIsPrinting(false);
      return;
    }

    const printWindow = window.open("", "_blank", "height=900,width=1100");
    if (!printWindow) {
      setIsPrinting(false);
      return;
    }

    const clonedHtml = source.outerHTML;

    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>Experian Credit Report</title>
          <style>${REPORT_CSS}</style>
        </head>
        <body>
          <div class="experian-modal-scroll">${clonedHtml}</div>
        </body>
      </html>`);
    printWindow.document.close();

    const printAfterAssets = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } finally {
        setTimeout(() => {
          printWindow.close();
          setIsPrinting(false);
        }, 450);
      }
    };

    const images = Array.from(printWindow.document.images || []);
    if (!images.length) {
      setTimeout(printAfterAssets, 150);
      return;
    }

    let pending = images.length;
    const done = () => {
      pending -= 1;
      if (pending <= 0) printAfterAssets();
    };

    images.forEach((image) => {
      if (image.complete) {
        done();
      } else {
        image.addEventListener("load", done, { once: true });
        image.addEventListener("error", done, { once: true });
      }
    });

    setTimeout(() => {
      if (pending > 0) printAfterAssets();
    }, 1600);
  };

  const reportStyleId = "experian-report-global-style";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(245,245,245,.92)",
        backdropFilter: "blur(2px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <style id={reportStyleId}>{REPORT_CSS}</style>

      <div className="experian-modal-scroll">
        {showError ? (
          <div className="exp-error-box no-print">
            <div><strong>Error:</strong> {String(errorMessage).replace(/_/g, " ")}</div>
            <div style={{ marginTop: 2 }}><strong>Code:</strong> {String(errorCode).replace(/_/g, " ")}</div>
          </div>
        ) : null}

        <ReportDocument report={ExperianReport || {}} />
      </div>

      <div className="exp-actions no-print">
        <button
          type="button"
          className="exp-action-btn exp-action-print"
          onClick={printReport}
          disabled={isPrinting}
        >
          {isPrinting ? "Preparing..." : "Print"}
        </button>
        <button
          type="button"
          className="exp-action-btn exp-action-close"
          onClick={onClose}
          disabled={isPrinting}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ExperianScoreReportModal;
